import React, { useState } from "react";
import { Trans } from "@lingui/macro";
import CruiseItineraryItem from "./CruiseItineraryItem";
import * as styles from "./CruiseItinerary.module.scss";

import PortShorexModal from "../PortExcursionModal/PortShorexModal";
import PortLandProgrammeModal from "../PortExcursionModal/PortLandProgrammeModal";
import { CruiseItineraryItemWithExcursions } from "./types";
import { BlockContentFragment } from "types/graphql-types";
import { isPresent } from "src/utils/checks";
import { BlockContent } from "components/BlockContent";

export interface CruiseItineraryProps {
  itinerary: CruiseItineraryItemWithExcursions[];
  cruiseType?: string;
  hideShorexesOnFirstDay?: boolean;
  hideShorexesOnLastDay?: boolean;
  overrideItineraryDisclaimer?: BlockContentFragment;
}

function buildItinerary(
  itinerary: CruiseItineraryItemWithExcursions[]
): {
  itineraryItem: CruiseItineraryItemWithExcursions;
  portSlideshowIndex: number;
}[] {
  const portsCacheCounter: Record<string, number> = {};
  return itinerary
    .filter((i) => i.port && i.port.data)
    .map((i) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const port = i.port!;
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const portCode = port.data!.code!;

      let lastPortSlideshowIndex = portCode
        ? portsCacheCounter[portCode]
        : undefined;

      if (lastPortSlideshowIndex == undefined || isNaN(lastPortSlideshowIndex))
        lastPortSlideshowIndex = -1;
      const portSlideshowIndex =
        (lastPortSlideshowIndex + 1) % (port?.portImages?.length || 1);
      portsCacheCounter[portCode] = portSlideshowIndex;

      return { itineraryItem: i, portSlideshowIndex };
    });
}

type OpenExcursionModalArgs = {
  type: "shorex" | "landProgramme";
  excursionId: number;
  duration?: number;
  hideDurationAndCode: boolean;
};
export type OpenExcursionModal = (args: OpenExcursionModalArgs) => void;

const disclaimerForCruiseType = {
  Classic: (
    <Trans id="shorexes-disclaimer-classic">
      The following excursions are provided as a sample of what may be offered
      on this voyage and are subject to change. We invite guests to visit{" "}
      <a href="https://my.silversea.com">my.silversea.com</a> 180 days prior to
      sailing for the most updated information.
    </Trans>
  ),
  Expedition: (
    <Trans id="shorexes-disclaimer-expedition">
      When travelling in remote regions tide, wind, ice and weather conditions
      determine the details of our itinerary. While we will do our best to
      maintain all suggested activities, some of these may be subject to change.
      Come with an open mind and a great sense of adventure, together we can
      turn any voyage into a wonderful Expedition. We invite guests to visit{" "}
      <a href="https://my.silversea.com">my.silversea.com</a> 180 days prior to
      sailing for the most updated information.
    </Trans>
  )
};

const CruiseItinerary: React.FC<CruiseItineraryProps> = ({
  itinerary,
  cruiseType,
  hideShorexesOnFirstDay = false,
  hideShorexesOnLastDay = false,
  overrideItineraryDisclaimer
}) => {
  const [openedExcursionModal, setOpenExcursionModal] = useState<
    OpenExcursionModalArgs | undefined
  >(undefined);

  const enhancedItinerary = React.useMemo(() => buildItinerary(itinerary), [
    itinerary
  ]);

  const itineraryDisclaimer = isPresent(overrideItineraryDisclaimer) ? (
    <BlockContent blocks={overrideItineraryDisclaimer} />
  ) : isPresent(cruiseType) ? (
    disclaimerForCruiseType[cruiseType]
  ) : null;

  return (
    <React.Fragment>
      <div data-content-name="section_itinerary">
        {enhancedItinerary.map(
          ({ itineraryItem, portSlideshowIndex }, index, values) => {
            const isFirst = index == 0;
            const isLast = index == values.length - 1;
            const hideShorexes =
              process.env.GATSBY_HIDE_SHOREXES == "true" ||
              (hideShorexesOnFirstDay && isFirst) ||
              (hideShorexesOnLastDay && isLast);

            return (
              <CruiseItineraryItem
                key={itineraryItem._key}
                item={itineraryItem}
                openExcursionModal={setOpenExcursionModal}
                portSlideshowIndex={portSlideshowIndex}
                hideShorexes={hideShorexes}
                shorexes={itineraryItem.shorexes}
                landProgrammes={itineraryItem.landProgrammes}
              />
            );
          }
        )}
        {openedExcursionModal && openedExcursionModal.type == "shorex" && (
          <PortShorexModal
            modal={{
              isOpen: !!openedExcursionModal,
              onRequestClose: () => setOpenExcursionModal(undefined)
            }}
            size="big"
            shorexId={openedExcursionModal.excursionId}
            duration={openedExcursionModal.duration}
            hideDurationAndCode={openedExcursionModal.hideDurationAndCode}
          />
        )}
        {openedExcursionModal && openedExcursionModal.type == "landProgramme" && (
          <PortLandProgrammeModal
            modal={{
              isOpen: !!openedExcursionModal,
              onRequestClose: () => setOpenExcursionModal(undefined)
            }}
            size="big"
            landProgrammeId={openedExcursionModal.excursionId}
          />
        )}
      </div>
      <div className={styles.shorexDisclaimer}>
        <p>{itineraryDisclaimer}</p>
      </div>
    </React.Fragment>
  );
};

export default React.memo(CruiseItinerary);
